import { type ReactElement, useState } from 'react'
import { DateTime } from 'luxon'
import { isDefined, isNotDefined, type ReportDateRange } from '@terros/common'
import { DatePicker } from '@mantine/dates'
import { Menu } from '@mantine/core'
import {
  DATE_RANGE_OPTION_GROUP_LABELS,
  findDateRangeOption,
  fromDateInput,
  getDateRangeOptionsByGroup,
  stringifyReportDateRange,
  useTranslation,
  type DateRangeOption,
  type DateRangeOptionGroup,
} from '@front/model'
import { MaterialSymbolIcon } from './terros/icon/MaterialSymbolIcon'
import { TButton } from './buttons'

const GROUPS: DateRangeOptionGroup[] = ['rolling', 'calendar', 'other']

type DateRangeMenuProps = {
  value: ReportDateRange | undefined
  onChange: (range: ReportDateRange) => void
  /**
   * Shown when `value` is unset, e.g. the inherited dashboard range a widget
   * falls back to. The menu still writes an explicit value when picked.
   */
  placeholder?: string
  /** Offers "Reset to dashboard range", clearing this level's own override. */
  onReset?: () => void
  resetLabel?: string
  disabled?: boolean
}

/**
 * Picks a `ReportDateRange` semantically — "Past 30 Days" is stored as a
 * relative range, not as the dates it resolved to today — with a calendar only
 * for genuinely fixed ranges.
 */
export function DateRangeMenu({
  value,
  onChange,
  placeholder,
  onReset,
  resetLabel = 'Reset to dashboard range',
  disabled,
}: DateRangeMenuProps): ReactElement {
  const { t } = useTranslation()
  const [isCustomOpen, setIsCustomOpen] = useState(false)
  const label = stringifyReportDateRange(value) ?? placeholder ?? 'Date range'
  const selectedKey = findDateRangeOption(value)?.key

  const handlePick = (option: DateRangeOption): void => {
    if (isNotDefined(option.value)) {
      setIsCustomOpen(true)
      return
    }
    setIsCustomOpen(false)
    onChange(option.value)
  }

  const handleCustomChange = (range: [string | null, string | null]): void => {
    const [start, end] = range
    if (isNotDefined(start) || isNotDefined(end)) return
    onChange({
      type: 'fixed',
      startTime: DateTime.fromISO(start).startOf('day').toMillis(),
      endTime: DateTime.fromISO(end).endOf('day').toMillis(),
    })
    setIsCustomOpen(false)
  }

  return (
    <Menu closeOnItemClick={false} position='bottom-start' shadow='md' width={290}>
      <Menu.Target>
        <TButton disabled={disabled} icon='calendar_month' iconPosition='left' size='sm' variant='gray'>
          {label}
        </TButton>
      </Menu.Target>
      <Menu.Dropdown mah={400} style={{ overflowY: 'auto' }}>
        {GROUPS.map((group) => (
          <div key={group}>
            <Menu.Label>{t(DATE_RANGE_OPTION_GROUP_LABELS.get(group) ?? group)}</Menu.Label>
            {getDateRangeOptionsByGroup(group).map((option) => (
              <Menu.Item
                key={option.key}
                rightSection={option.key === selectedKey ? <MaterialSymbolIcon icon='check' size='sm' /> : undefined}
                onClick={() => handlePick(option)}
              >
                {t(option.label)}
              </Menu.Item>
            ))}
          </div>
        ))}
        {isCustomOpen && (
          <div className='p-2'>
            <DatePicker
              allowSingleDateInRange
              type='range'
              defaultValue={toPickerValue(value)}
              onChange={handleCustomChange}
            />
          </div>
        )}
        {isDefined(onReset) && (
          <>
            <Menu.Divider />
            <Menu.Item
              leftSection={<MaterialSymbolIcon icon='restart_alt' size='sm' />}
              onClick={() => {
                setIsCustomOpen(false)
                onReset()
              }}
            >
              {t(resetLabel)}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}

function toPickerValue(range: ReportDateRange | undefined): [string | null, string | null] {
  if (isNotDefined(range) || range.type !== 'fixed') return [null, null]
  return [fromDateInput(range.startTime).toISODate(), fromDateInput(range.endTime).toISODate()]
}
